import { NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import React from 'react';
import { AppElement, AppFC } from '../../../../interfaces';
import { AuthToken } from '../../../../services';
import { Paths } from '../../../../constants';

export const TermsOfUse: AppFC = (): AppElement => {
  const authToken: AuthToken = new AuthToken();
  const isAuth = !!authToken.getAccessToken() && !!authToken.getRefreshToken();

  return (
    <>
      <Helmet>
        <title>Cryptolocally: Terms of Use</title>
      </Helmet>
      <div className="flex flex-col flex-1 h-auto w-screen bg-white text-black [&_h3]:leading-[2rem] [&_p]:text-[1.25rem] [&_p]:leading-[2.25rem] [&_p]:font-normal [&_a]:text-[1.25rem] [&_a]:leading-[2.25rem] [&_a]:font-normal [&_a]:text-tertiary">
        <div className={`pt-14 pb-10  ${!isAuth ? 'px-[7%]' : 'px-[3%]'}`}>
          <h2 className="font-semibold pb-2">Terms of Use</h2>
          <p>Everything you need to know.</p>
        </div>
        <div className={`flex flex-col gap-10 pt-8 pb-24 bg-[#f2f5f9] ${!isAuth ? 'px-[7%]' : 'px-[3%]'}`}>
          <div className="flex flex-col">
            <h3 className="mb-4">1 These terms</h3>
            <p>
              1.1 These terms of use govern your use and access to our services, including our website(s), our application(s), our application programming interfaces (APIs), our notifications and any information or content appearing therein (collectively our “Platform”).
            </p>
            <p>
              1.2 By using our Platform, you agree to these terms regardless of whether you are paying user or a non-paying visitor. If you are using our Platform as a representative of an entity, you are agreeing to these terms on behalf of that entity.
            </p>
            <p>
              1.3 You should also read our Privacy Policy which sets out how we collect and use your personal information.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">2 About us and how to contact us</h3>
            <p>
              2.1 For any questions or problems relating to our Platform, our products or services, or these terms, you can contact us by emailing us at support@cryptolocally.com.
            </p>
            <p>
              2.2 Please refer to our
              {' '}
              <NavLink to={Paths.PrivacyPolicy} className="text-tertiary">Privacy Policy</NavLink>
              {' '}
              for information about what personal data we collect and what we do with it. It is important that you read our
              privacy policy, which may be updated from time to time, and understand how we use your information and the rights that you have about your information.
            </p>
            <p>
              2.3 If we have to contact you, we will do so by telephone or by writing to you at the email address or postal address you provided to us.
            </p>
            <p>
              2.4 When we use the words &quot;writing&quot; or &quot;written&quot; in these terms, this includes emails.
            </p>

          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">3 Changes of terms</h3>
            <p>
              3.1 We may amend these terms from time to time by posting the updated terms on our Platform. If we make material changes, we will notify you of the changes before they become effective. By continuing to use our Platform and our services after the changes come into effect means that you agree to be bound by the revised policy.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">4 Availability of our services</h3>
            <p>
              4.1 We are constantly changing and improving our Platform and the products or services we provide. We may from time to time change or discontinue any of the products or services we offer, or add or remove functionalities or features, and we may suspend or stop certain products, services, functionalities or features altogether. If we discontinue certain products, services, functionalities or features, we will give you advance notice where reasonably possible.
            </p>
            <p>
              4.2 We may release products, services, functionalities or features that we are still testing and evaluating. We will label such services as “beta”, “preview”, “early access” or “trial” or any words or phrases with similar meanings. You understand that these beta services are not as reliable as other products or services we offer.
            </p>
            <p>
              4.3 We reserve the right to limit your use of our Platform and the services we provide, including the right to restrict, suspend or terminate your account if we believe you are in breach of these terms or are misusing our Platform or any services we provide.
            </p>
            <p>
              4.4 We try our best to ensure that our Platform is always available, but we do not guarantee that the operation of or access to our Platform will be uninterrupted or continuous. Our Platform may be interrupted for maintenance, repairs, upgrades, network or equipment failures.
            </p>
            <p>
              4.5 You are responsible for configuring your information technology, computer programmes and platform or system in order to access our Platform. We do not guarantee that our Platform will be free from bugs or viruses.
            </p>
            <p>
              4.6 If the Platform is informed that any Digital Assets or funds held in your Platform Account are stolen, or illegitimately, illegally, or fraudulently acquired, or otherwise are not lawfully possessed by you, the Platform may, but has no obligation to, place an administrative hold on the affected funds and your Platform Account. If the Platform does lay down an administrative hold on some or all of your funds or the Platform Account, the Platform may continue such hold until such time as the dispute has been resolved and evidence of the resolution acceptable to the Platform has been provided to the Platform in a form acceptable to the Platform. The Platform will not involve itself in any such dispute or the resolution of the dispute. You agree that the Platform will have no liability or responsibility for any such hold, or for your inability to withdraw Digital Assets or funds or execute trades during the period of any such hold.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">5 Your account and password</h3>
            <p>
              5.1 In registering for an account on our Platform, you must provide truthful, accurate and up-to-date information about yourself. You should choose a strong and secure password. You must keep your password secure and confidential.
            </p>
            <p>
              5.2 You agree not to share your account credentials or give others access to your account. If and when we detect that an account is shared by multiple users, we may treat this as a security breach and suspend or terminate your account.
            </p>
            <p>
              5.3 We have the right to disable any password, whether chosen by you or allocated by us, at any time, if in our reasonable opinion you are in breach of these terms.
            </p>
            <p>
              5.4 You agree to follow our Acceptable Use Policy, which is set out at the end of these terms.
            </p>
            <p>
              5.5 You are responsible for all actions or activities that happens by, through or under your account, unless you report misuse.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">6 Use of the platform</h3>
            <p>
              6.1 You must comply with the Acceptable Use Policy and all applicable laws and regulatory requirements, including privacy laws and intellectual property laws in using or accessing the Platform.
            </p>
            <p>
              6.2 Subject to your payment of applicable fees, we give you a personal, worldwide, royalty-free, non-assignable, non-transferrable, non-sublicensable, non-exclusive and revocable licence to access and use our Platform, including any software or application as part of the services we offer. This licence is for the sole purpose of enabling you to use and enjoy the benefit of our Platform as provided by us and in the manner as permitted by these terms.
            </p>
            <p>
              6.3 This licence to use our Platform will terminate if you do not comply with these terms or other additional terms or conditions imposed by us from time to time.
            </p>
            <p>
              6.4 You must not copy, modify, distribute, sell, lease, loan or trade any access to the Platform or any data or information on it.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">7 Your rights</h3>
            <p>
              7.1 You retain your rights to any information or content you submit, post or display on or through the Platform (“Your Content”). By submitting, posting or displaying such content, you grant us a worldwide, non-exclusive, royalty-free licence (with the right to sublicense) to use, process, copy, reproduce, adapt, modify, publish, transmit, display and distribute such content in any and all media or through any distribution channels (now known or later developed), subject to the applicable provisions in our Privacy Policy.
            </p>
            <p>
              7.2 You are responsible for your use of Your Content and any consequences thereof, including any consequences of the use of Your Content by other users or third parties. We are not responsible or liable for any use of Your Content, nor the use of any content or information submitted or posted by other users or visitors.
            </p>
            <p>
              7.3 You warrant that Your Content is not and will not infringe rights of any third parties and that you have all the necessary rights, power and authority to satisfy your obligations with regard to Your Content under these terms.
            </p>
            <p>
              7.4 If you believe your intellectual property rights have been infringed, please contact us by emailing us at admin@cryptolocally.com .
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">8 Our rights</h3>
            <p>
              8.1 All intellectual property rights subsisting in the Platform or the products or services we provide belong to us or have been lawfully licensed to us. All rights under applicable laws are hereby reserved. You must not upload, post, publish, reproduce, transmit or distribute any content or component of our Platform in any way, or create any derivative works with respect to any such content or component.
            </p>
            <p>
              8.2 We may (at our discretion but are not obliged to) review content or information submitted or posted by users on our Platform. We reserve the right to remove any content which we consider as offensive, harmful, deceptive, discriminative, defamatory or otherwise inappropriate or misleading, or content that we believe may be infringing rights of third parties. We do not endorse or support any views expressed by any users on our Platform.
            </p>
            <p>
              8.3 Our name “CryptoLocally” and our marks and logos are our trade marks (be it registered or unregistered) and may not be used without our express prior written consent.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">9 Integrations</h3>
            <p>
              9.1 We integrate with third-party software to provide a full suite of functionalities to our users. We are not responsible for any issues or loss arising from the use of any third-party software. Your access and use of the third-party software is governed by the terms of service or user agreements of that software.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">10 Feedback</h3>
            <p>
              10.1 We value and welcome feedback on our Platform. You agree that we are free to use, disclose, adopt and/or modify any feedback and any information (including any ideas, concepts, proposals, suggestions or comments) provided by you to use in connection with our Platform or any products or services we offer, without any payment to you.
            </p>
            <p>
              10.2 You hereby waive and agree to waive any rights to claim for any fees, royalties, charges or other payments in relation to our use, disclosure, adoption and/or modification of any of your feedback.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">11 Limitation on liabilities</h3>
            <p>
              11.1 Some countries or jurisdictions may not allow the disclaimers in this clause, in which case these disclaimers will not apply to you.
            </p>
            <p>
              11.2 To the fullest extent permitted by law, we (including our holding company(ies), subsidiaries, affiliates, directors, officers, employees, agents, representatives, partners and licensors (collectively, “Our Entities”)) expressly limit our liabilities in connection with or arising out of the provision of the Platform as follows:
            </p>
            <p>
              (a) we provide the Platform and any products or services we offer on an “as is” and “as available” basis, and your access to or use of our Platform is at your own risk;
            </p>
            <p>
              (b) we give no assurance, representation or warranty of any kind (whether express or implied) about the Platform and any products or services we provide;
            </p>
            <p>
              (c) we do not guarantee that the information or content you find on the Platform is always accurate, truthful, complete and up-to-date;
            </p>
            <p>
              (d) we expressly disclaim all warranties and representations (for example, warranties of merchantability, fitness for a particular purpose, and non-infringement);
            </p>
            <p>
              (e) we are not responsible for any delay or disruption in our Platform or any defect, viruses, bugs or errors; and
            </p>
            <p>
              (f) we are not responsible for the conduct of or any content or information submitted or posted by any user of the Platform (whether online or offline).
            </p>
            <p>
              11.3 To the fullest extent permitted by law, Our Entities are not liable to you or others for:
            </p>
            <p>
              (a) any indirect, incidental, special, exemplary, consequential or punitive damages; or
            </p>
            <p>
              (b) any loss of data, business, opportunities, reputation, profits or revenues,
            </p>
            <p>
              (c) relating to the use of our Platform or any products or services we offer.
            </p>
            <p>
              11.4 We do not exclude or limit our liability to you where it would be illegal to do so. This includes any of our liability for fraud or making fraudulent misrepresentation in operating the Platform or providing the products or services we offer.
            </p>
            <p>
              11.5 If you are using the Platform as a consumer, in some countries or jurisdictions you may have certain legal rights as a consumer. In such cases, nothing in these terms limit your legal rights as a consumer that may not be waived by contract.
            </p>
            <p>
              11.6 Other than the types of liabilities that we cannot limit by law, the liabilities of Our Entities to you (on aggregate) are limited to the amount you have paid us (if any) for the use of our Platform or for any products or services we offer over the last twelve (12) months.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">12 Your representation</h3>
            <p>
              12.1 Our Platform is not intended for and may not be used by minors. By using our Platform, you represent that you are an adult and that you are able to legally enter into contractual agreements.
            </p>
            <p>
              12.2 If you are using the Platform on behalf of an entity, by using the Platform you represent that you have the necessary rights and authority to agree to these terms (and our Privacy Policy, Acceptable Use Policy and other documents referred to herein) on behalf of that entity.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">13 Indemnity</h3>
            <p>
              13.1 You agree to indemnify and hold Our Entities harmless from and against all liabilities, damages, claims, costs (including legal fees and costs), and expenses in connection with or arising from (i) your breach of these terms, (ii) your use of our Platform and/or (iii) any misrepresentation made by you.
            </p>
            <p>
              13.2 You also agree to fully co-operate with us in the defence or settlement of any claim in relation to or arising out of our Platform or these terms.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">14 Termination</h3>
            <p>
              14.1 These terms will continue to apply until terminated by either you or us as follows.
            </p>
            <p>
              14.2 You may stop using the Platform any time by deactivating your account.
            </p>
            <p>
              14.3 We reserve the right to suspend or terminate your access to our Platform, if we reasonably believe:
            </p>
            <p>
              (a) you are in serious or repeated breach of these terms (including a prolonged failure to settle any payment);
            </p>
            <p>
              (b) you are using the Platform in a manner that would cause a real risk of harm or loss to us, other users, or the public;
            </p>
            <p>
              (c) we are requested to do so by government or regulatory authorities or as required under applicable laws, regulations or legal processes; or
            </p>
            <p>
              (d) our provision of the Platform to you is no longer possible or commercially viable.
            </p>
            <p>
              In any of the above cases, we will notify you by the email address associated with your account or at the next time you attempt to access your account, unless we are prohibited from notifying you by law.
            </p>
            <p>
              14.4 Upon termination of your access, these terms will also terminate except for Clauses 11 to 18.
            </p>
            <p>
              14.5 Where we consider necessary or appropriate, we will report any breach of these terms (or the Acceptable Use Policy) to law enforcement authorities and we will cooperate with such authorities by disclosing your identity and providing any information about you within our systems to them.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">15 Entire agreement</h3>
            <p>
              15.1 These terms constitute the entire agreement between any user and us in relation to the use of or any transactions on the Platform. These terms supersede and extinguish all other agreements, promises, assurances, warranties, representations and understandings between any user and us, whether written or oral, in relation to the use of or any transactions on the Platform.
            </p>
            <p>
              15.2 You acknowledge that you will have no remedies in respect of any statement, representation, assurance or warranty (whether made innocently or negligently) that is not set out in these terms.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">16 Other important terms</h3>
            <p>
              16.1 We may transfer our rights and obligations under these terms to another organisation. We will contact you to let you know if we plan to do this. If you do not wish to continue the contract with the transferee, you may contact us to end the contract within one (1) calendar month of us informing you of the proposed transfer and we will refund you any payments you have made in advance for any products not provided.
            </p>
            <p>
              16.2 You may only transfer your rights or your obligations under these terms to another person if we agree to this in writing.
            </p>
            <p>
              16.3 If a court finds part of this contract illegal, the rest will continue in force. Each of the paragraphs of these terms operates separately. If any court or relevant authority decides that any of them are unlawful, the remaining paragraphs will remain in full force and effect.
            </p>
            <p>
              16.4 Even if we delay in enforcing this contract, we can still enforce it later. If we do not insist immediately that you do anything you are required to do under these terms, or if we delay in taking steps against you in respect of your breaking this contract, that will not mean that you do not have to do those things and it will not prevent us taking steps against you at a later date. For example, if you miss a payment and we do not chase you but we continue to provide the products, we can still require you to make the payment at a later date.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">17 Contact</h3>
            <p>
              17.1 If you have any questions about these terms or the Acceptable Use Policy, please contact us by support@cryptolocally.com.
            </p>
          </div>
          <div className="flex flex-col">
            <h3 className="mb-4">18 Governing law and jurisdiction</h3>
            <p>
              18.1 These terms are governed by and shall be construed in accordance with the laws of the Hong Kong Special Administrative Region.
            </p>
            <p>
              18.2 The courts of the Hong Kong Special Administrative Region shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these terms.
            </p>
            <p>
              Acceptable Use Policy
            </p>
            <p>
              18.3 As part of the terms of use, you agree not to misuse the Platform or help anyone else to do so. For example, you agree not to do any of the following in connection with the Platform:
            </p>
            <p>
              (a) use our Platform for unlawful or unauthorised purposes;
            </p>
            <p>
              (b) re-sell or attempt to benefit in a commercial fashion from any data, content or information available on the Platform;
            </p>
            <p>
              (c) probe, scan, or test the vulnerability of any system or network;
            </p>
            <p>
              (d) breach or otherwise circumvent any security or authentication measures or service use limits;
            </p>
            <p>
              (e) access, tamper with, or use non-public areas or parts of the Platform;
            </p>
            <p>
              (f) interfere with or disrupt any user, host, or network, for example by sending a virus, trojan, worm, logic bomb, or any other material that is malicious or technologically harmful, overloading, flooding, spamming, or mail-bombing any part of the Platform, or by scripting the creation of any content in such manner as to interfere with or create an undue burden on the Platform;
            </p>
            <p>
              (g) reverse engineer, decompile, disassemble, decipher or otherwise attempt to derive the source code for the Platform or any related technology that is not open source;
            </p>
            <p>
              (h) access, search, or create accounts for the Platform by any means (automated or otherwise) other than our publicly supported interfaces (for example, &quot;scraping&quot; or creating accounts in bulk) or attempt to do so;
            </p>
            <p>
              (i) send unsolicited communications, promotions or advertisements, or spam;
            </p>
            <p>
              (j) forge any TCP/IP packet header or any part of the header information in any email;
            </p>
            <p>
              (k) send altered, deceptive, or false source-identifying information, including &quot;spoofing&quot; or &quot;phishing&quot;;
            </p>
            <p>
              (l) conduct surveys, contests, or pyramid schemes, or promote or advertise products or services without appropriate authorisation;
            </p>
            <p>
              (m) abuse referrals or promotions;
            </p>
            <p>
              (n) post, publish, upload, display, distribute, or share materials that are unlawful, inappropriate, profane, pornographic, obscene, indecent, libelous, defamatory, abusive, or knowingly false, and/or that infringe intellectual property rights;
            </p>
            <p>
              (o) violate the letter or spirit of our terms of use;
            </p>
            <p>
              (p) violate applicable laws or regulations in any way; or
            </p>
            <p>
              (q) violate the privacy or infringe the rights of others.
            </p>
          </div>
          <h3>
            Last updated: 4 July 2024
          </h3>
        </div>
      </div>
    </>
  );
};
